import reference from './aiAutomation.json';
import { abs, ORG_ID } from './siteConfig';
import { breadcrumbJsonLd } from './schema';

export type RichText = { text: string; to?: string; em?: boolean; strong?: boolean }[];
export type AutomationCard = { number: string; title: string; body: RichText; tag: string; to?: string | null; pick: boolean };
export type AutomationSection = {
  type: string; id?: string | null; eyebrow: string; heading: RichText; paragraphs: RichText[];
  ctas: {text: string; to: string}[]; bullets?: string[]; credential?: string;
  cards?: AutomationCard[]; steps?: {number: string; title: string; body: RichText}[];
  faqs?: {q: string; a: string}[];
  tracks?: {title: string; steps: {time: string; body: RichText}[]}[];
  beforeAfter?: {beforeTitle: string; before: string[]; afterTitle: string; after: RichText[]};
  nodes?: {number: string; text: string}[];
};
export type AutomationPage = {path: string; title: string; label: string; description: string; sections: AutomationSection[]};
export const automationPages = reference as Record<'overview' | 'receptionist' | 'workflows' | 'software' | 'brain', AutomationPage>;
export type AutomationKey = keyof typeof automationPages;
export const automationLinks = [
  {to: '/ai-automation/receptionist', label: 'AI receptionist', description: 'Every lead answered, 24/7'},
  {to: '/ai-automation/workflows', label: 'Workflow automation', description: 'Leads, quotes, invoices'},
  {to: '/ai-automation/custom-software', label: 'Custom software', description: 'Dashboards, agents, tools'},
  {to: '/ai-automation/company-brain', label: 'Company brain', description: 'Your whole business, one system'},
  {to: '/ai-automation', label: 'Overview', description: 'How it fits with your ads'},
];
export function automationHead(key: AutomationKey) {
  const page = automationPages[key];
  const url = abs(page.path);
  const metaDescription = key === 'brain'
    ? 'A company brain connects your leads, jobs, ads, calendar and team, then answers questions, writes the morning brief and flags what got dropped.'
    : page.description;
  return {
    meta: [
      {title: page.title}, {name: 'description', content: metaDescription},
      {property: 'og:title', content: page.title}, {property: 'og:description', content: metaDescription},
      {property: 'og:type', content: 'website'}, {property: 'og:url', content: url},
      {name: 'twitter:card', content: 'summary_large_image'},
      {name: 'twitter:title', content: page.title}, {name: 'twitter:description', content: metaDescription},
    ],
    links: [{rel: 'canonical', href: url}],
    scripts: [
      {type: 'application/ld+json', children: breadcrumbJsonLd([
        {name: 'Home', path: '/'}, {name: 'AI & Automation', path: '/ai-automation'},
        ...(key === 'overview' ? [] : [{name: page.label, path: page.path}]),
      ])},
      {type: 'application/ld+json', children: JSON.stringify({
        '@context': 'https://schema.org', '@type': key === 'overview' ? 'WebPage' : 'Service',
        '@id': `${url}#${key === 'overview' ? 'webpage' : 'service'}`, name: page.label === 'Overview' ? 'AI & Automation' : page.label,
        url, description: page.description,
        ...(key === 'overview' ? {about: {'@id': ORG_ID}} : {provider: {'@id': ORG_ID}}),
      })},
      {type: 'application/ld+json', children: JSON.stringify({
        '@context': 'https://schema.org', '@type': 'FAQPage', '@id': `${url}#faq`,
        mainEntity: page.sections.flatMap(s => s.faqs ?? []).map(f => ({
          '@type': 'Question', name: f.q, acceptedAnswer: {'@type': 'Answer', text: f.a},
        })),
      })},
    ],
  };
}

export function setupMessage(picks: string[]) { return picks.length ? `AI & Automation: ${picks.join(', ')}` : ''; }